import { ChangeEvent, FC } from 'react'
import { Box, Typography, styled } from '@mui/material'
import { yupResolver } from '@hookform/resolvers/yup'
import * as Yup from 'yup'
import { Controller, useForm } from 'react-hook-form'
import { Button, FloatingInput } from '@icanbwell/ui-toolkit'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useResendMfaSmsMutation } from '@client-hub/store/api/crs/react'
import { schemas } from '@client-hub/store/api/crs/types'
import ClientLogo from 'components/ClientLogo'
import { MFA_METHOD } from '@client-hub/shared/constants'
import { HEADER_HEIGHT } from '@client-hub/shared/constants/styles'
import { useMfaNavigateState } from 'hooks'

import { useTranslation } from 'react-i18next'

const Wrapper = styled(Box)({
  minHeight: `calc(100vh - ${HEADER_HEIGHT})`,
  maxWidth: '420px',
  paddingTop: '3rem',
  display: 'flex',
  flexDirection: 'column',
  margin: '0 auto',
})

const LinkButton = styled(Button)({
  minWidth: 0,
  maxWidth: 'none',
  textDecoration: 'underline',
  whiteSpace: 'nowrap',
  '&:hover': {
    textDecoration: 'underline',
  },
})

export type MfaNavigateState = {
  uid: string
  totp_url?: string
  phone_number?: string
  two_factor_auth?: number
  sms_status?: schemas['MultiFactorAuthEnableResponseSchema']['sms_status']
  from?: Location
  isAllowedMfa?: boolean
}

export type MfaAuthContentType = {
  placeholder: string
  back: { name: string; url?: string; callback?: () => void }
  switchButton?: { name: string; url: string }
  label?: string
  subText?: string
  showQrCode?: boolean
  showLogo?: boolean
}

export type MfaAuthProps = {
  content: MfaAuthContentType
  schema: Yup.SchemaOf<{ code: string }>
  onSubmit: (code: string) => void
  onChange: (e: ChangeEvent<HTMLInputElement>, onChangeController: (...event: unknown[]) => void) => void
}

const MfaAuth: FC<MfaAuthProps> = ({ content, schema, onSubmit, onChange }) => {
  const { t } = useTranslation()

  const location = useLocation()
  const navigate = useNavigate()

  const { phone_number, totp_url, two_factor_auth, uid } = useMfaNavigateState()
  const [resendSms] = useResendMfaSmsMutation()
  const {
    control,
    handleSubmit,
    formState: { isSubmitting, isValid },
  } = useForm<Yup.TypeOf<typeof schema>>({
    mode: 'onChange',
    resolver: yupResolver(schema),
    defaultValues: schema.cast({}),
  })

  const handleForm = handleSubmit(({ code }) => onSubmit(code || ''))

  const handleChange = (onChangeController: (...event: unknown[]) => void) => (e: ChangeEvent<HTMLInputElement>) =>
    onChange(e, onChangeController)

  const handleBack = () => {
    content.back.callback?.()
    content.back.url ? navigate(content.back.url) : navigate(-1)
  }

  const handleResendSms = () => resendSms({ uid, phone_number })

  return (
    <Wrapper>
      {content.showLogo && <ClientLogo variant="dark" />}
      <Typography
        component="h1"
        sx={{
          color: 'common.primaryBase',
          fontSize: '1.7rem',
          lineHeight: '2.1rem',
          margin: '2rem 0 1rem',
        }}
      >
        {t('routes.mfaAuth.global.title')}
      </Typography>
      {content.showQrCode && (
        <Typography component="p" mb="0.5rem">
          {t('routes.mfaAuth.auth.label')}
        </Typography>
      )}
      {content.showQrCode && (
        <Box display="flex" justifyContent="center">
          <img src={totp_url} alt={t('routes.mfaAuth.auth.qrCode')} />
        </Box>
      )}
      <Typography component="p" mb="0.5rem">
        {content.label}
      </Typography>
      <Box component="form" autoComplete="off" onSubmit={handleForm}>
        <Controller
          name="code"
          control={control}
          render={({ field: { ref, onChange, ...field } }) => (
            <FloatingInput
              {...field}
              disabled={isSubmitting}
              label={content.placeholder}
              onChange={handleChange(onChange)}
              inputRef={ref}
            />
          )}
        />
        {content.subText && <Box mt="0.5rem">{content.subText}</Box>}
        <Box display="flex" flexDirection="column" alignItems="center">
          <Button style={{ marginTop: '2rem' }} disabled={!isValid || isSubmitting} type="submit">
            {t('routes.mfaAuth.global.submit')}
          </Button>
          <Button style={{ marginTop: '1rem' }} variant="text" onClick={handleBack}>
            {content.back.name}
          </Button>
          {two_factor_auth === MFA_METHOD.SMS && (
            <Box display="flex" alignItems="center">
              <p>{t('routes.mfaAuth.global.noCode')}</p>
              <LinkButton variant="text" size="small" onClick={handleResendSms} style={{ fontWeight: 400 }}>
                {t('routes.mfaAuth.global.resendCode')}
              </LinkButton>
            </Box>
          )}
          {content.switchButton && (
            <Link to={content.switchButton.url} state={location.state}>
              {content.switchButton.name}
            </Link>
          )}
        </Box>
      </Box>
    </Wrapper>
  )
}

export default MfaAuth
